import type { Id } from './common';

// Hana as an MCP client (AI S6): the company's catalogue of outside MCP servers (spec §6.1, §11.1), their tools
// (§6.3), and (T13) the connection DTOs.

/** How a person signs in to the server: OAuth, a personal token, or nothing (spec §5.1 `auth_kind`). */
export const EXT_AUTH_KINDS = ['oauth', 'token', 'none'] as const;
export type ExtAuthKind = (typeof EXT_AUTH_KINDS)[number];

/** One live server of the catalogue. Never a secret or its ciphertext: `hasClientSecret` only. */
export type ExtServerDTO = {
  id: Id;
  name: string;
  url: string;
  authKind: ExtAuthKind;
  /** `token` servers only; null = `Authorization: Bearer <token>`. */
  tokenHeader: string | null;
  /** `oauth` servers only: the client the admin entered. */
  clientId: string | null;
  hasClientSecret: boolean;
  refusesSensitive: boolean;
  enabled: boolean;
  toolsRefreshedAt: string | null;
  /** Live connections with status `active` (spec §6.1: the admin sees the count, nothing else). */
  activeConnections: number;
  /** Tools in status `new` or `changed`. */
  toolsAwaitingReview: number;
  version: number;
};

export type ExtServerListDTO = {
  /** False when the deployment has no AI_SECRET_KEY: no client secret (and no personal token) can be sealed. */
  secretKeyConfigured: boolean;
  servers: ExtServerDTO[];
};

export type ExtServerCreateBody = {
  name: string;
  url: string;
  authKind: ExtAuthKind;
  tokenHeader?: string | null;
  clientId?: string | null;
  clientSecret?: string | null;
  refusesSensitive?: boolean;
  enabled?: boolean;
};

/**
 * Absent keeps. `clientSecret`: a string replaces, null clears. Changing `url` or `authKind` is refused while anyone is
 * connected (D21).
 */
export type ExtServerPatchBody = { version: number } & Partial<ExtServerCreateBody>;

export type ExtServerProbeBody = { url: string };

/**
 * "Kiểm tra" (spec §6.1): `initialize` without a token. `oauth` says what the authorization server offers;
 * `needsClientId` = the admin must enter a client id (neither a usable CIMD nor DCR, D13).
 */
export type ExtProbeResultDTO =
  | { result: 'ok' }
  | { result: 'oauth'; cimd: boolean; dcr: boolean; needsClientId: boolean }
  | { result: 'token' }
  | { result: 'unreachable' }
  | { result: 'not_mcp' };

// Tools (spec §6.3, §11.1) ------------------------------------------------------------------------------------------

/** Chạy luôn / Luôn xác nhận / Tắt. */
export const EXT_TOOL_RISKS = ['read', 'confirm', 'off'] as const;
export type ExtToolRisk = (typeof EXT_TOOL_RISKS)[number];

/** Mới / Đã duyệt / Đã đổi / Không còn / Không dùng được. */
export const EXT_TOOL_STATUSES = ['new', 'approved', 'changed', 'gone', 'unusable'] as const;
export type ExtToolStatus = (typeof EXT_TOOL_STATUSES)[number];

/** Most entries one `PATCH …/tools` may carry (the 500-tool list cap). */
export const EXT_TOOLS_PATCH_MAX = 500;

/**
 * One tool of a server's catalogue, with its full schemas: the detail page renders every nested `description` and
 * `title`, and diffs `inputSchema` against `pendingSchema` itself. `inputSchema` is the approved schema, or the latest
 * one seen while the tool is not approved yet; `pendingSchema` is the version waiting for approval of a `changed` tool
 * (null otherwise). `description` is the server's text cut at 2 000 characters.
 */
export type ExtToolDTO = {
  name: string;
  title: string | null;
  description: string;
  hintReadOnly: boolean;
  risk: ExtToolRisk;
  status: ExtToolStatus;
  inputSchema: Record<string, unknown>;
  pendingSchema: Record<string, unknown> | null;
  /** D23: the version an approval takes (`new` and `changed` only); an approving PATCH entry sends it back as `hash`. */
  pendingHash: string | null;
  approvedAt: string | null;
  /** S7 §5.5, §9.2: "Cho tự gửi" — Hana may send it by herself for a person who chose it (a `confirm` tool only). */
  autonomyAllowed: boolean;
};

export type ExtToolListDTO = { tools: ExtToolDTO[] };

/**
 * A risk per tool. A risk other than `off` approves: the current schema of a `new` tool, the pending one of a
 * `changed` tool, and then needs `hash` — the `pendingHash` the admin was shown (D23). `off`, or a risk on an
 * `approved` tool, sets the risk only. A `gone` or `unusable` tool, an unknown name or a missing `hash` is refused
 * (422); a `hash` that is no longer the version waiting refuses the whole save (409 `TOOL_CHANGED`, `names`).
 */
export type ExtToolsPatchBody = { tools: { name: string; risk: ExtToolRisk; hash?: string;
  /** S7 §5.5 "Cho tự gửi": absent keeps it; `true` with a risk other than `confirm` → 422. Leaving `confirm` clears it. */
  autonomyAllowed?: boolean }[] };

// Connections (spec §6.2, §6.4, §11.2) -------------------------------------------------------------------------------

/** `active`, or `expired` (Cần đăng nhập lại). A revoked connection is no connection at all. */
export type ExtConnectionStatus = 'active' | 'expired';

/** The caller's own live connection to one server. Never a token or its ciphertext. */
export type ExtConnectionDTO = {
  id: Id;
  status: ExtConnectionStatus;
  /** OAuth only, when the server named the account (D14). */
  accountLabel: string | null;
  /** Token servers only (absent otherwise): the last four characters of the token (D28). */
  tokenLastFour?: string | null;
  lastUsedAt: string | null;
  inBriefing: boolean;
};

/** One enabled server of the catalogue, as a card (spec §6.2). */
export type ExtConnectionServerDTO = { serverId: Id; name: string; authKind: ExtAuthKind;
  connection: ExtConnectionDTO | null };

/** `GET /api/assistant/connections` (spec §11.2, §11.3). Enabled, live servers ordered by name. */
export type ExtConnectionsDTO = { servers: ExtConnectionServerDTO[] };

/** `PUT /api/assistant/connections/:serverId/token` (token servers only). Trimmed, 1–4 096 characters. */
export type ExtTokenBody = { token: string };
export const EXT_TOKEN_MAX = 4096;

/** `PATCH /api/assistant/connections/:serverId`. */
export type ExtConnectionPatchBody = { inBriefing: boolean };

/**
 * `POST /api/assistant/connections/:serverId/start` (D26): a `none` server is connected at once; an OAuth server
 * answers where the browser goes next (T8).
 */
export type ExtConnectStartDTO = { connected: true } | { authorizeUrl: string };

/** The `start` body: where the browser comes back to after an OAuth login (an internal path; else the default).
 *  S8 §9 (D85): the "Kết nối" tab of `/assistant/me`. The 026 column default still says `/settings/assistant#connections`;
 *  the web redirect of that route covers a stored row (D83). */
export type ExtConnectStartBody = { returnTo?: string };
export const EXT_RETURN_TO_DEFAULT = '/assistant/me?tab=ket-noi#connections';

/**
 * `?ext_error=<code>` on the page an OAuth callback returns to (spec §6.2 step 5). Stable; the page maps each to
 * Vietnamese (T13). `needs_client_id` is also what `start` answers (409 EXT_NEEDS_CLIENT_ID) when no client fits.
 * `session`: the callback came back with no session (D32) — to the default page; the login can be finished after
 * signing in again, until its state expires.
 */
export const EXT_CONNECT_ERRORS = ['state', 'denied', 'iss', 'server_changed', 'needs_client_id', 'token', 'unreachable',
  'not_mcp', 'session'] as const;
export type ExtConnectError = (typeof EXT_CONNECT_ERRORS)[number];

/** Why a connection was revoked (spec §6.4), as the `ai.ext_connection.revoke` audit records it. */
export const EXT_REVOKE_REASONS = ['user', 'password_changed', 'password_reset', 'account_disabled', 'ai_disabled',
  'server_removed'] as const;
export type ExtRevokeReason = (typeof EXT_REVOKE_REASONS)[number];

/** What the connect routes answer, in Vietnamese (spec §6.2). */
export const EXT_CONNECT_MESSAGES = {
  token: 'Token không dùng được với máy chủ này',
  tokenLength: 'Token có từ 1 đến 4.096 ký tự',
  unreachable: 'Không kết nối được máy chủ, thử lại sau',
  serverChanged: 'Máy chủ vừa thay đổi, hãy tải lại rồi kết nối lại',
  wrongKind: 'Máy chủ này không dùng kiểu kết nối này',
  needsClientId: 'Máy chủ cần client id — nhờ admin nhập',
  oauthInvalid: 'Máy chủ khai báo đăng nhập OAuth không hợp lệ',
} as const;

/** §13, §9: `ingest.from_external` on a ref this conversation never stored (not read, expired, another chat's). */
export const EXT_RESOURCE_NOT_READY = 'EXT_RESOURCE_NOT_READY';
export const EXT_RESOURCE_NOT_READY_MESSAGE = 'Đọc file trước bằng resources/read.';
/** §9: a ref an earlier `ingest.from_external` already put into a batch. */
export const EXT_RESOURCE_CLAIMED_MESSAGE = 'File này đã được đưa vào một lô.';
